// src/lib/consoleCapture.js
//
// Ring buffer of the most recent console.error / window.error /
// unhandledrejection events. Read by the Report Bug modal so a non-technical
// reporter automatically ships the last few errors with their report.
//
// Loaded FIRST in main.jsx so we catch errors from React's mount onward.
// Lives on window.__bugCtx so the modal can read it without an import cycle.

const MAX = 15

const buffer = []

function push(entry) {
  buffer.push({ ts: new Date().toISOString(), ...entry })
  if (buffer.length > MAX) buffer.shift()
}

export function getRecentErrors() {
  return buffer.slice()
}

export function clearRecentErrors() {
  buffer.length = 0
}

let installed = false

export function installConsoleCapture() {
  if (installed) return
  installed = true

  const origError = console.error.bind(console)
  console.error = (...args) => {
    try {
      const msg = args.map(a => {
        if (a instanceof Error) return a.stack || a.message
        if (typeof a === 'string') return a
        try { return JSON.stringify(a) } catch { return String(a) }
      }).join(' ')
      push({ level: 'error', message: msg.slice(0, 1000) })
    } catch {}
    origError(...args)
  }

  // Window-level error and unhandledrejection are already wired in main.jsx for
  // reportClientError; mirror them here too so the bug-report buffer has the
  // same view even if reporting is rate-limited / deduplicated.
  window.addEventListener('error', (e) => {
    push({
      level: 'window_error',
      message: (e.message || 'Window error').slice(0, 500),
      stack:   (e.error?.stack || '').slice(0, 1500),
    })
  })

  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || String(e.reason) || 'Unhandled rejection'
    push({
      level: 'unhandled_rejection',
      message: msg.slice(0, 500),
      stack:   (e.reason?.stack || '').slice(0, 1500),
    })
  })

  // Expose on window for debug + the modal to read.
  try { window.__bugCtx = { getRecentErrors, clearRecentErrors } } catch {}
}
